import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { updateItinerary, deleteItinerary, cloneItinerary } from '../services/api';
import cacheService from '../services/cacheService';
import Navbar from '../components/Navbar';
import MobileAppBanner from '../components/MobileAppBanner';
import Toast from '../components/Toast';
import SettingsModal from '../components/SettingsModal';
import styled from 'styled-components';
import { format, max, isValid, isPast } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import AdPageLayout from '../components/AdPageLayout';
import TripCreationModal from '../components/TripCreationModal';


const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafb 0%, #f1f5f7 100%);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const PageTitle = styled.h1`
  color: #1a365d;
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 700;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #316C6E 0%, #254F51 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  margin: 0;
  font-weight: 400;
`;

const ItineraryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ItineraryCard = styled(Link)`
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04);
    border-color: rgba(49, 108, 110, 0.1);
  }

  &:active {
    transform: translateY(-4px);
  }
`;

const ItineraryContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ItineraryImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const ItineraryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${ItineraryCard}:hover & {
    transform: scale(1.05);
  }
`;

const ExpiredOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, rgba(107, 114, 128, 0.9), rgba(75, 85, 99, 0.9));
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(2px);
`;

const ExpiredText = styled.span`
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const CardBody = styled.div`
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ItineraryName = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1a365d;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ItineraryDates = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  margin: 0 0 12px 0;
  font-weight: 500;
`;

const ItineraryLastUpdated = styled.p`
  font-size: 0.85rem;
  color: #94a3b8;
  margin: auto 0 0 0;
  font-weight: 400;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const PaginationButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 15px;
  margin: 0 5px;
  border-radius: 4px;
  cursor: pointer;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const SettingsIcon = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  color: white;
  cursor: pointer;
  z-index: 3;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.5);
    transform: rotate(90deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const StatusIndicator = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 6px 12px;
  border-radius: 20px;
  z-index: 2;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => {
    switch (props.status) {
      case 'expired':
        return '#94a3b8'; // slate-400
      case 'upcoming':
        return '#3b82f6'; // blue-500
      case 'active':
        return '#10b981'; // emerald-500
      default:
        return '#94a3b8';
    }
  }};
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
`;

const StatusText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  text-transform: capitalize;
  letter-spacing: 0.025em;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(237.5px, 1fr));
  gap: 20px;
`;

const SkeletonCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  height: 100%;
  position: relative;
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 142.5px;
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonText = styled.div`
  height: ${props => props.height || '20px'};
  width: ${props => props.width || '80%'};
  margin: ${props => props.margin || '10px auto'};
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
`;

const SkeletonLoading = () => {
  return (
    <SkeletonGrid>
      {[...Array(8)].map((_, index) => (
        <SkeletonCard key={index}>
          <SkeletonImage />
          <div style={{ padding: '10px' }}>
            <SkeletonText height="24px" width="70%" margin="15px auto" />
            <SkeletonText height="16px" width="90%" margin="10px auto" />
          </div>
          <div style={{ marginTop: 'auto' }}>
            <SkeletonText height="14px" width="60%" margin="15px auto" />
          </div>
        </SkeletonCard>
      ))}
    </SkeletonGrid>
  );
};

const CreateTripCard = styled(ItineraryCard)`
  background: linear-gradient(135deg, #fafbfc 0%, #f1f7f7 100%);
  border: 2px dashed #316C6E;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 32px 20px;
  text-decoration: none;
  color: #316C6E;
  min-height: 300px;
  
  &:hover {
    background: linear-gradient(135deg, #f1f7f7 0%, #e8f2f2 100%);
    border-color: #254F51;
    color: #254F51;
    transform: translateY(-4px);
  }

  svg {
    font-size: 3rem;
    margin-bottom: 16px;
    opacity: 0.8;
  }
`;

const CreateTripText = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.4;
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7f9;
`;

const MyTrips = () => {
  const [itineraries, setItineraries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTripCreationModal, setShowTripCreationModal] = useState(false);
  const itemsPerPage = 7;
  const { language } = useLanguage();
  const t = translations[language].pages.myTrips;

  const fetchItineraries = useCallback(async () => {
    const startTime = Date.now();
    try {
      const response = await cacheService.getMyTrips();
      const processedItineraries = response.map(itinerary => ({
        ...itinerary,
        lastUpdated: calculateLastUpdated(itinerary)
      }));
      const sortedItineraries = processedItineraries.sort((a, b) => 
        b.lastUpdated.getTime() - a.lastUpdated.getTime()
      );
      setItineraries(sortedItineraries);
      setError(null);
    } catch (err) {
      console.error('Error fetching itineraries:', err);
      setError('Failed to load itineraries. Please try again later.');
    } finally {
      const endTime = Date.now();
      const loadingTime = endTime - startTime;
      const minimumLoadingTime = 1000; // 1 second in milliseconds

      if (loadingTime < minimumLoadingTime) {
        setTimeout(() => {
          setIsLoading(false);
        }, minimumLoadingTime - loadingTime);
      } else {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchItineraries();
    
    // Subscribe to cache invalidation events
    const unsubscribe = cacheService.subscribe('MY_TRIPS', (action) => {
      console.log(`🔔 Cache event received: MY_TRIPS ${action}`);
      // Refresh trips data when cache is invalidated
      fetchItineraries();
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, [fetchItineraries]);

  const calculateLastUpdated = (itinerary) => {
    const dates = [
      new Date(itinerary.updatedAt),
      ...itinerary.pois.map(poi => new Date(poi.updatedAt)),
      ...(itinerary.customActivities || []).map(activity => new Date(activity.updatedAt))
    ].filter(date => isValid(date));

    return dates.length > 0 ? max(dates) : new Date();
  };

  const handleSettingsClick = (itinerary, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedItinerary(itinerary);
    setShowSettingsModal(true);
  };

  const handleCloseSettingsModal = () => {
    setShowSettingsModal(false);
    setSelectedItinerary(null);
  };

  const handleSaveSettings = async (updatedTitle, updatedDateRange) => {
    try {
      await cacheService.updateTrip(selectedItinerary.id, {
        name: updatedTitle,
        start_date: updatedDateRange.from,
        end_date: updatedDateRange.to
      });
      setToastMessage(t.settings.updateSuccess);
      setShowToast(true);
      // fetchItineraries will be called automatically via cache event
    } catch (error) {
      console.error('Error updating trip:', error);
      setToastMessage(t.settings.updateError);
      setShowToast(true);
    }
    handleCloseSettingsModal();
  };

  const handleDeleteItinerary = async () => {
    try {
      await cacheService.deleteTrip(selectedItinerary.id);
      setToastMessage(t.settings.deleteSuccess);
      setShowToast(true);
      // fetchItineraries will be called automatically via cache event
    } catch (error) {
      console.error('Error deleting trip:', error);
      setToastMessage(t.settings.deleteError);
      setShowToast(true);
    }
    handleCloseSettingsModal();
  };

  const handleCloneItinerary = async () => {
    try {
      await cloneItinerary(selectedItinerary.id);
      setToastMessage(t.settings.cloneSuccess);
      setShowToast(true);
      // Manually invalidate cache since cloneItinerary doesn't use cache service
      cacheService.invalidate('MY_TRIPS');
    } catch (error) {
      console.error('Error copying trip:', error);
      setToastMessage(t.settings.cloneError);
      setShowToast(true);
    }
    handleCloseSettingsModal();
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItineraries = itineraries.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getItineraryImage = (itinerary) => {
    if (itinerary.pois && itinerary.pois.length > 0 && itinerary.pois[0].image_url) {
      let imageUrl = itinerary.pois[0].image_url;
      if (typeof imageUrl === 'string' && imageUrl.startsWith('[')) {
        const parsedImages = JSON.parse(imageUrl);
        if (parsedImages.length > 0) {
          return parsedImages[0];
        }
      }
      return imageUrl;
    }
    return 'https://storage.googleapis.com/quisqueyaquest-resources/icons/QQ-Big.png';
  };

  const formatDate = (date) => {
    if (!date || !isValid(new Date(date))) {
      return 'Invalid Date';
    }
    return format(new Date(date), 'MMM d, yyyy');
  };

  const formatDateTime = (date) => {
    if (!date || !isValid(new Date(date))) {
      return 'Invalid Date';
    }
    return format(new Date(date), 'MMM d, yyyy HH:mm');
  };

  const isExpired = (endDate) => {
    // Create a date for yesterday (end of day)
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999); // End of yesterday
    
    return new Date(endDate) < yesterday;
  };

  const getTripStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Create a date for yesterday (end of day)
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999); // End of yesterday

    // Only mark as expired if the trip ended yesterday or earlier
    if (end < yesterday) {
      return 'expired';
    } else if (start > now) {
      return 'upcoming';
    } else {
      return 'active';
    }
  };

  const handleCreateTrip = async (tripData) => {
    try {
      await cacheService.createTrip({
        name: 'New Trip',
        ...tripData
      });
      setToastMessage(t.settings.createSuccess);
      setShowToast(true);
      setShowTripCreationModal(false);
      // fetchItineraries will be called automatically via cache event
    } catch (error) {
      console.error('Error creating trip:', error);
      setToastMessage(t.settings.createError);
      setShowToast(true);
    }
  };

  return (
    <PageWrapper>
      <Navbar />
      <AdPageLayout>
        <PageContainer>
          <PageHeader>
            <PageTitle>{t.title}</PageTitle>
            <PageSubtitle>Plan and manage your Dominican Republic adventures</PageSubtitle>
          </PageHeader>
          {isLoading ? (
            <SkeletonLoading />
          ) : error ? (
            <p>{error}</p>
          ) : (
            <ItineraryGrid>
              <CreateTripCard as="div" onClick={() => setShowTripCreationModal(true)}>
                <FontAwesomeIcon icon={faPlus} />
                <CreateTripText>{t.createNew}</CreateTripText>
              </CreateTripCard>
              {currentItineraries.map(itinerary => (
                <ItineraryCard key={itinerary.id} to={`/trip/${itinerary.id}`}>
                  <ItineraryImageContainer>
                    <ItineraryImage 
                      src={getItineraryImage(itinerary)} 
                      alt={itinerary.name} 
                    />
                    <StatusIndicator>
                      <StatusDot status={getTripStatus(itinerary.start_date, itinerary.end_date)} />
                      <StatusText>
                        {t.status[getTripStatus(itinerary.start_date, itinerary.end_date)]}
                      </StatusText>
                    </StatusIndicator>
                    <SettingsIcon onClick={(e) => handleSettingsClick(itinerary, e)}>
                      <FontAwesomeIcon icon={faCog} />
                    </SettingsIcon>
                    {isExpired(itinerary.end_date) && (
                      <ExpiredOverlay>
                        <ExpiredText>{t.expired}</ExpiredText>
                      </ExpiredOverlay>
                    )}
                  </ItineraryImageContainer>
                  <CardBody>
                    <ItineraryName>{itinerary.name}</ItineraryName>
                    <ItineraryDates>
                      {formatDate(itinerary.start_date)} - {formatDate(itinerary.end_date)}
                    </ItineraryDates>
                    <ItineraryLastUpdated>
                      {t.lastUpdated}: {formatDateTime(itinerary.lastUpdated)}
                    </ItineraryLastUpdated>
                  </CardBody>
                </ItineraryCard>
              ))}
            </ItineraryGrid>
          )}
          {!isLoading && !error && itineraries.length > itemsPerPage && (
            <PaginationContainer>
              <PaginationButton 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                {t.previous}
              </PaginationButton>
              <PaginationButton 
                onClick={() => paginate(currentPage + 1)} 
                disabled={indexOfLastItem >= itineraries.length}
              >
                {t.next}
              </PaginationButton>
            </PaginationContainer>
          )}
        </PageContainer>
      </AdPageLayout>
      {showToast && (
        <Toast
          message={toastMessage}
          onClose={() => setShowToast(false)}
          type="success"
        />
      )}
      {showSettingsModal && selectedItinerary && (
        <SettingsModal
          show={showSettingsModal}
          onClose={handleCloseSettingsModal}
          title={selectedItinerary.name}
          dateRange={{
            from: new Date(selectedItinerary.start_date),
            to: new Date(selectedItinerary.end_date)
          }}
          onTitleChange={(e) => setSelectedItinerary({...selectedItinerary, name: e.target.value})}
          onDateChange={(range) => setSelectedItinerary({...selectedItinerary, start_date: range.from, end_date: range.to})}
          onSave={() => handleSaveSettings(selectedItinerary.name, {from: selectedItinerary.start_date, to: selectedItinerary.end_date})}
          onDelete={handleDeleteItinerary}
          onClone={handleCloneItinerary}
          hasUnsavedChanges={true}
        />
      )}
      {showTripCreationModal && (
        <TripCreationModal
          onClose={() => setShowTripCreationModal(false)}
          onCreateTrip={handleCreateTrip}
        />
      )}
      <MobileAppBanner />
    </PageWrapper>
  );
};

export default MyTrips;
